/**
 * PublicAnalyticsPage
 *
 * Mounted at /site/:domain. Renders the full Orbit dashboard for anonymous
 * viewers when the site owner has flipped `is_public=true` in website settings.
 *
 * Implementation:
 *   1. Resolve the route's :domain → website_id via /api/public/{domain}.
 *      That endpoint enforces `is_public=true` server-side and 403s otherwise.
 *   2. Once we have the id, render <AnalyticsPage publicMode .../>. AnalyticsPage
 *      drops its Authorization headers when no token is present and renders a
 *      slim brand topbar (logo + Login / Sign Up) instead of the in-app
 *      HeaderBar. The backend analytics endpoints accept unauthenticated
 *      requests via get_current_user_optional + check_website_permission's
 *      public-site bypass, so all dashboard tiles + filters work normally.
 *
 * Why a wrapper (vs. duplicating AnalyticsPage's 1300+ lines):
 *   - Single source of truth for tiles, charts, tables, filters, click-handlers
 *   - Any future analytics feature shows up here for free
 *   - All public-mode chrome differences are contained in AnalyticsPage's
 *     `publicMode` branches (search for `publicMode` to see exactly what flips)
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Lock } from 'lucide-react';
import AnalyticsPage from './AnalyticsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicAnalyticsPage() {
    const { domain } = useParams();
    // resolved.website_id comes from the backend; resolved.routeDomain is the
    // *cleaned* domain we typed into the URL (e.g. "yogabox.com"), NOT the raw
    // DB value (which may be "https://www.yogabox.com" with scheme + www and
    // would break routeBase navigation by inserting slashes into /site/...).
    const [resolved, setResolved] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const abort = new AbortController();

        const resolveDomain = async () => {
            if (!domain) {
                setError('No domain provided');
                setLoading(false);
                return;
            }
            // Strip any accidental scheme / www / trailing slash users might paste.
            const cleanDomain = domain
                .replace(/^https?:\/\//, '')
                .replace(/^www\./, '')
                .replace(/\/$/, '');
            try {
                const res = await axios.get(`${API}/public/${cleanDomain}`, {
                    signal: abort.signal,
                });
                // Backend returns { website_id, domain } only when is_public=true.
                // Keep the cleanDomain separately so filter-click navigations stay at
                // /site/yogabox.com instead of /site/https://www.yogabox.com.
                setResolved({
                    website_id: res.data.website_id,
                    displayDomain: res.data.domain,
                    routeDomain: cleanDomain,
                });
                setLoading(false);
            } catch (err) {
                if (axios.isCancel(err)) return;
                if (err.response?.status === 403) {
                    setError("This website's analytics are private");
                } else if (err.response?.status === 404) {
                    setError('Website not found');
                } else {
                    setError('Failed to load analytics');
                }
                setLoading(false);
            }
        };

        resolveDomain();
        return () => abort.abort();
    }, [domain]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <Lock className="w-6 h-6 text-red-600" />
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
                    <p className="text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    if (!resolved) {
        return null;
    }

    // Delegate to the full dashboard in publicMode. AnalyticsPage will use
    // websiteIdOverride to bypass its useParams() read (the route param here is
    // :domain, not :websiteId), and domainOverride seeds the site name + the
    // route base used by filter-click navigations so the URL stays at /site/{domain}.
    return (
        <AnalyticsPage
            user={null}
            token={null}
            logout={() => {}}
            publicMode
            websiteIdOverride={resolved.website_id}
            domainOverride={resolved.routeDomain}
        />
    );
}
